import type { Carrier } from "@shared-core/data/supplier-portal-adapter";
import type { PickDespatchInput } from "@shared-core/data/supplier-picks-adapter";

/** What the warehouse fills in for a despatch, as the form holds it. Shared by the list's bulk form and the pick page. */
export interface DespatchFields {
  ref: string;
  date: string;
  carrier: Carrier | "";
  carrierName: string;
  tracking: string;
}

export function emptyDespatchFields(today: string): DespatchFields {
  return { ref: "", date: today, carrier: "", carrierName: "", tracking: "" };
}

/** The wire entry for one pick (and, from the pick page, one destination, quantity and batch). */
export function despatchEntry(
  pickId: string,
  fields: DespatchFields,
  extra: Pick<
    PickDespatchInput,
    "delivery_line_id" | "quantity" | "batch_id"
  > = {},
): PickDespatchInput {
  // The form validates nothing a rule of ours decides: an unchosen carrier goes as it is and the server says so.
  const carrier = fields.carrier as Carrier;
  return {
    pick_id: pickId,
    despatch_ref: fields.ref.trim(),
    despatched_on: fields.date,
    carrier,
    carrier_name: carrier === "other" ? fields.carrierName.trim() : null,
    tracking_number:
      carrier === "own_van" || !fields.tracking.trim()
        ? null
        : fields.tracking.trim(),
    ...extra,
  };
}

/** The input fields a refusal can name; anything else is shown as a plain line above the form's button. */
export const DESPATCH_FIELD_NAMES = [
  "despatch_ref",
  "despatched_on",
  "carrier",
  "carrier_name",
  "tracking_number",
  "delivery_line_id",
  "quantity",
  "batch_id",
] as const;

/**
 * A refusal names fields by the request body's path (`despatches.2.despatch_ref`), but the form has one set of
 * inputs for every entry it sent: fold each error onto its field name, the first message winning.
 */
export function foldDespatchErrors(
  fieldErrors: Record<string, string>,
): Record<string, string> {
  const folded: Record<string, string> = {};
  for (const [path, message] of Object.entries(fieldErrors)) {
    const field = path.replace(/^despatches\.\d+\./, "");
    folded[field] ??= message;
  }
  return folded;
}

/** The folded errors that belong to no input of the form, to be shown as lines of their own. */
export function unplacedDespatchErrors(
  folded: Record<string, string>,
): string[] {
  return Object.entries(folded)
    .filter(
      ([field]) => !(DESPATCH_FIELD_NAMES as readonly string[]).includes(field),
    )
    .map(([, message]) => message);
}
